import { useEffect, useRef } from 'react';

export default function Footer() {
  const containerRef = useRef<HTMLDivElement>(null);
  const connectRef = useRef<HTMLDivElement>(null);
  const servicesRef = useRef<HTMLDivElement>(null);
  const logoRef = useRef<HTMLDivElement>(null);
  const positionLogoRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    function positionLogo() {
      if (!containerRef.current || !connectRef.current || !servicesRef.current || !logoRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      if (containerRect.width === 0) return;

      const connectRect = connectRef.current.getBoundingClientRect();
      const servicesRect = servicesRef.current.getBoundingClientRect();
      const logoRect = logoRef.current.getBoundingClientRect();

      const connectRight = connectRect.right - containerRect.left;
      const servicesLeft = servicesRect.left - containerRect.left;
      const midpoint = (connectRight + servicesLeft) / 2;

      const logoWidth = logoRect.width;
      const logoLeft = midpoint - logoWidth / 2;

      logoRef.current.style.left = `${logoLeft}px`;
    }

    positionLogoRef.current = positionLogo;
    positionLogo();

    window.addEventListener('resize', positionLogo);
    const timer = setTimeout(positionLogo, 300);

    return () => {
      window.removeEventListener('resize', positionLogo);
      clearTimeout(timer);
    };
  }, []);

  const logoUrl = 'https://storage.helloreaddy.io/project_files/b4f3eeda-f893-4987-8921-4636c63c4dc6/3299c8f9-c928-4a8a-ae42-534bde1cd785_compressed_Logo-2-White.webp';

  const connectColumn = (
    <div className="text-left">
      <h4 className="text-lg font-normal mb-4 text-[#FBF7F0]">
        Connect
      </h4>
      <div className="space-y-3 text-[#FBF7F0]">
        <div className="flex items-center justify-start gap-3">
          <a href="https://wa.me/message/WQKZAK5P3C77F1" className="w-8 h-8 bg-[#D6794E] rounded-full flex items-center justify-center hover:bg-[#385B47] transition-colors cursor-pointer shrink-0 group">
            <i className="ri-whatsapp-line text-white text-sm"></i>
          </a>
          <span>WhatsApp: <a href="https://wa.me/message/WQKZAK5P3C77F1" className="text-[#D6794E] hover:text-[#FBF7F0] underline cursor-pointer">@yuvatiart</a></span>
        </div>
        <div className="flex items-center justify-start gap-3">
          <a href="https://www.instagram.com/yuvati_art/" target="_blank" rel="noopener noreferrer" className="w-8 h-8 bg-[#D6794E] rounded-full flex items-center justify-center hover:bg-[#385B47] transition-colors cursor-pointer shrink-0 group">
            <i className="ri-instagram-line text-white text-sm"></i>
          </a>
          <span>Instagram: <a href="https://www.instagram.com/yuvati_art/" target="_blank" rel="noopener noreferrer" className="text-[#D6794E] hover:text-[#FBF7F0] underline cursor-pointer">@yuvati_art</a></span>
        </div>
        <div className="flex items-center justify-start gap-3">
          <a href="mailto:yuliya@yuvati.art" className="w-8 h-8 bg-[#D6794E] rounded-full flex items-center justify-center hover:bg-[#385B47] transition-colors cursor-pointer shrink-0 group">
            <i className="ri-mail-line text-white text-sm"></i>
          </a>
          <span>Email: <a href="mailto:yuliya@yuvati.art" className="text-[#D6794E] hover:text-[#FBF7F0] underline cursor-pointer">yuliya@yuvati.art</a></span>
        </div>
        <div className="flex items-center justify-start gap-3">
          <a href="https://blog.yuvati.art" target="_blank" rel="noopener noreferrer" className="w-8 h-8 bg-[#D6794E] rounded-full flex items-center justify-center hover:bg-[#385B47] transition-colors cursor-pointer shrink-0 group">
            <i className="ri-book-open-line text-white text-sm"></i>
          </a>
          <span>Blog: <a href="https://blog.yuvati.art" target="_blank" rel="noopener noreferrer" className="text-[#D6794E] hover:text-[#FBF7F0] underline cursor-pointer">Read articles</a></span>
        </div>
        <div className="flex items-start justify-start gap-3">
          <a href="https://maps.app.goo.gl/Xzb18kuv6cTjqYWC8" target="_blank" rel="noopener noreferrer" className="w-8 h-8 bg-[#D6794E] rounded-full flex items-center justify-center hover:bg-[#385B47] transition-colors cursor-pointer shrink-0 mt-0.5 group">
            <i className="ri-map-pin-line text-white text-sm"></i>
          </a>
          <div className="flex flex-col">
            <span>Yuvati Art ~ Art Therapy &amp; Counseling</span>
            <a href="https://maps.app.goo.gl/Xzb18kuv6cTjqYWC8" target="_blank" rel="noopener noreferrer" className="text-[#D6794E] hover:text-[#FBF7F0] underline cursor-pointer text-sm whitespace-nowrap">📍 In-person location</a>
          </div>
        </div>
      </div>
    </div>
  );

  const servicesColumn = (
    <div className="text-center lg:text-left">
      <h4 className="text-lg font-normal mb-4 text-[#FBF7F0]">
        Services
      </h4>
      <ul className="space-y-3 text-[#FBF7F0]">
        <li className="flex items-center justify-center lg:justify-start min-h-[32px]">Individual Art Therapy</li>
        <li className="flex items-center justify-center lg:justify-start min-h-[32px]">Group Sessions</li>
        <li className="flex items-center justify-center lg:justify-start min-h-[32px]">Learn Neurographica™</li>
        <li className="flex items-center justify-center lg:justify-start min-h-[32px]">Addiction Counseling</li>
        <li className="flex items-center justify-center lg:justify-start min-h-[32px]">In-Person & Online Sessions</li>
      </ul>
    </div>
  );

  const logoImg = (
    <div
      role="img"
      aria-label="Yuvati logo"
      className="h-[260px] w-[160px]"
      style={{
        backgroundColor: '#D6794E',
        WebkitMaskImage: `url(${logoUrl})`,
        maskImage: `url(${logoUrl})`,
        WebkitMaskSize: 'contain',
        maskSize: 'contain',
        WebkitMaskRepeat: 'no-repeat',
        maskRepeat: 'no-repeat',
        WebkitMaskPosition: 'center',
        maskPosition: 'center',
      }}
    />
  );

  return (
    <footer id="contact" className="bg-[#385B47] pt-8 pb-12 border-t border-[#3D5C4A]">
      <div className="max-w-7xl mx-auto px-6 lg:px-28">
        {/* Desktop: two columns with absolutely positioned logo between them */}
        <div
          ref={containerRef}
          className="hidden lg:flex justify-between items-start mb-8 relative"
        >
          <div ref={connectRef}>
            {connectColumn}
          </div>
          <div
            ref={logoRef}
            className="absolute top-1/2 -translate-y-1/2"
            style={{ left: '50%' }}
          >
            {logoImg}
          </div>
          <div ref={servicesRef}>
            {servicesColumn}
          </div>
        </div>

        {/* Mobile: stack vertically */}
        <div className="flex flex-col lg:hidden items-center mb-8">
          <div className="w-full">
            {connectColumn}
          </div>
          <div className="my-6">
            {logoImg}
          </div>
          {servicesColumn}
        </div>

        <div className="border-t border-[#3D5C4A] pt-8 text-center">
          <p className="text-[#FBF7F0] text-sm">
            © 2026 Yuvati Art Company Limited. Registered in Thailand, No. 0505567017184.
          </p>
        </div>
      </div>
    </footer>
  );
}